import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillButton } from '../../components/site/Pill';
import { useSchemes, SchemeCard } from '../../components/site/SchemeGrid';
import { openSiteChat } from '../../components/SiteChat';
import { schemeImage, INTERIORS } from '../../lib/site';
import { Reveal } from '../../components/site/Reveal';
import { ListingCard, type Listing } from './ListingCard';
import type { ListMeta } from '../../types/api';

const BUDGETS = [1200, 1500, 1800, 2200, 2800];

/**
 * Locations. On lotusliving.co.uk this page is a list of schemes with an enquiry form;
 * here it is the live homes-to-rent search — the same searchListings service the staff
 * page and the AI tool use, so a home that lets this morning disappears this morning.
 */
export default function SiteListings() {
  const [params, setParams] = useSearchParams();
  const scheme = params.get('scheme') ?? '';
  const [filters, setFilters] = useState<Record<string, string>>({ city: params.get('city') ?? '', bedrooms: '', maxRent: '', availableFrom: '', q: '' });
  const [page, setPage] = useState(1);

  const schemes = useSchemes();
  const current = schemes.data?.find((s) => s.slug === scheme);

  const query = useMemo(() => Object.fromEntries(Object.entries({ ...filters, propertySlug: scheme || undefined, page, perPage: 12, sort: 'rent' }).filter(([, v]) => v !== '' && v !== undefined)), [filters, scheme, page]);
  const { data, isLoading } = useQuery({ queryKey: ['public-listings', query], queryFn: async () => api.public.get<Listing[]>('/public/listings', query) });
  const meta = data?.meta as ListMeta | undefined;

  const set = (k: string, v: string) => { setPage(1); setFilters({ ...filters, [k]: v }); };
  const clearScheme = () => { params.delete('scheme'); params.delete('soon'); setParams(params); setPage(1); };
  const cities = useMemo(() => Array.from(new Set((schemes.data ?? []).map((s) => s.city).filter(Boolean))).sort() as string[], [schemes.data]);
  const active = Object.values(filters).some(Boolean) || !!scheme;

  return (
    <SiteLayout>
      {/* Scheme header when arriving from a scheme card */}
      {current ? (
        <section className="relative overflow-hidden bg-ink-900 text-white">
          <img src={current.coverUrl ?? schemeImage(current.slug)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900/90 via-ink-900/60 to-ink-900/20" />
          <div className="site-container relative z-10 py-16 sm:py-20 lg:py-28">
            <button onClick={clearScheme} className="rise mb-6 inline-flex min-h-[44px] items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.18em] text-white/70 hover:text-white" style={{ '--i': 0 } as React.CSSProperties}><X className="h-3.5 w-3.5" /> All locations</button>
            <p className="rise eyebrow !text-bronze-300" style={{ '--i': 1 } as React.CSSProperties}>{current.city}{current.region === 'AE' ? ' · Dubai' : ''}</p>
            <h1 className="rise h-page mt-3" style={{ '--i': 2 } as React.CSSProperties}>{current.name}</h1>
            {current.description && <p className="rise lede mt-5 max-w-xl text-white/80" style={{ '--i': 3 } as React.CSSProperties}>{current.description}</p>}
            {!!current.amenities?.length && (
              <ul className="rise mt-6 flex flex-wrap gap-2" style={{ '--i': 4 } as React.CSSProperties}>
                {current.amenities.map((a) => <li key={a} className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[12px] text-white/90"><Check className="h-3 w-3 text-bronze-300" />{a}</li>)}
              </ul>
            )}
          </div>
        </section>
      ) : (
        <section className="site-container page-head">
          <h1 className="rise h-page max-w-[900px] text-ink-900" style={{ '--i': 0 } as React.CSSProperties}>Discover</h1>
        </section>
      )}

      {/* Filter bar: a stacked card on phones and tablets, one pill from 1024px (index.css .site-search). */}
      <section className="rise site-container mt-10 sm:mt-12" style={{ '--i': 3 } as React.CSSProperties}>
        <div className="site-search">
          <div className="site-search-main relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input value={filters.q} onChange={(e) => set('q', e.target.value)} placeholder="Search area, scheme or feature" aria-label="Search" className="site-input site-search-control w-full !pl-11" />
          </div>
          {!scheme && (
            <Sel width="lg:w-[11rem]" label="Location" value={filters.city} onChange={(v) => set('city', v)}>
              <option value="">Anywhere</option>{cities.map((c) => <option key={c}>{c}</option>)}
            </Sel>
          )}
          <Sel width="lg:w-[10rem]" label="Size" value={filters.bedrooms} onChange={(v) => set('bedrooms', v)}>
            <option value="">Any size</option><option value="0">Studio</option><option value="1">1 bed</option><option value="2">2 beds</option><option value="3">3 beds</option>
          </Sel>
          <Sel width="lg:w-[12.5rem]" label="Budget" value={filters.maxRent} onChange={(v) => set('maxRent', v)}>
            <option value="">Any budget</option>{BUDGETS.map((r) => <option key={r} value={r}>Max £{r.toLocaleString()}</option>)}
          </Sel>
          <label className={`site-search-date relative ${scheme ? 'col-span-full lg:col-auto' : ''}`}>
            <span className="sr-only">Move in by</span>
            <input type="date" value={filters.availableFrom} onChange={(e) => set('availableFrom', e.target.value)} title="Move in by" className={`site-input site-search-control ${filters.availableFrom ? '' : 'is-empty'}`} />
            {!filters.availableFrom && <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] text-ink-500 sm:text-[15px]">Move in by</span>}
          </label>
        </div>
        {active && (
          <div className="mt-3 flex justify-end text-[13px]">
            <button onClick={() => { setFilters({ city: '', bedrooms: '', maxRent: '', availableFrom: '', q: '' }); clearScheme(); }} className="min-h-[44px] font-medium text-ink-900 hover:text-bronze-600">Clear filters</button>
          </div>
        )}
      </section>

      {/* Results */}
      <section className="site-container section-b mt-6 sm:mt-8">
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-14">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-[10px] bg-ink-100" />)
            : data?.data.length
              ? data.data.map((l, i) => <Reveal key={l.id} className="min-w-0" delay={(i % 3) * 110}><ListingCard listing={l} index={i} image={cardImage(data.data, i)} /></Reveal>)
              : (
                <div className="col-span-full rounded-[10px] border border-dashed border-ink-200 px-6 py-16 text-center sm:py-20">
                  <p className="h-card text-ink-900">{current?.comingSoon ? 'Coming soon' : 'Nothing matches just now'}</p>
                  <p className="mx-auto mt-3 max-w-md text-sm text-ink-500">
                    {current?.comingSoon
                      ? `${current.name} is still being built. Our assistant can register your interest and tell you what is available nearby today.`
                      : 'Try widening your search, or ask our assistant — it can tell you what is coming up before it is listed.'}
                  </p>
                  <PillButton onClick={openSiteChat} variant="ghost" className="mt-8">Talk to the assistant</PillButton>
                </div>
              )}
        </div>
        {meta && meta.totalPages > 1 && (
          <div className="mt-14 flex flex-wrap justify-center gap-2">
            {Array.from({ length: meta.totalPages }).map((_, i) => (
              <button key={i} onClick={() => { setPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`h-10 w-10 rounded-full text-sm transition ${page === i + 1 ? 'bg-ink-900 text-white' : 'border border-ink-200 text-ink-600 hover:border-ink-900'}`}>{i + 1}</button>
            ))}
          </div>
        )}
      </section>

      {/* Other schemes */}
      {!!schemes.data?.length && (
        <section className="border-t border-ink-100 bg-white">
          <div className="site-container section-y">
            <Reveal as="h2" className="h-section text-ink-900">{current ? <>More locations</> : <>Our schemes</>}</Reveal>
            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-10 sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
              {schemes.data.filter((s) => s.slug !== scheme).slice(0, 8).map((s, i) => <Reveal key={s.id} className="min-w-0" delay={(i % 4) * 90}><SchemeCard scheme={s} index={i} /></Reveal>)}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}

/** The first home at a scheme shows the building; the rest cycle through interiors so a page of one scheme is not one photo nine times. */
const INTERIOR_CYCLE = [INTERIORS.livingArea, INTERIORS.bedroom, INTERIORS.kitchen, INTERIORS.harlow, INTERIORS.kitchenDetail, INTERIORS.bathroom];
function cardImage(rows: Listing[], i: number): string | undefined {
  const slug = rows[i]?.property?.slug;
  const priorSame = rows.slice(0, i).filter((r) => r.property?.slug === slug).length;
  return priorSame === 0 ? undefined : INTERIOR_CYCLE[(priorSame - 1) % INTERIOR_CYCLE.length];
}

function Sel({ label, width, value, onChange, children }: { label: string; width: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className={`min-w-0 ${width}`}>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className="site-select site-search-control w-full">
        {children}
      </select>
    </div>
  );
}
